import { useSyncExternalStore } from 'react';

export type ExternalStore<T> = {
  get: () => T;
  set: (next: T) => void;
  subscribe: (listener: () => void) => () => void;
  useValue: () => T;
};

/**
 * A module-scoped store consumed reactively via `useSyncExternalStore`, replacing Apollo
 * `makeVar` reactive vars for client-only state as Apollo is torn out of SLW.
 *
 * - `useValue()` subscribes a component so it re-renders when the value changes.
 * - `get`/`set` let non-React callers read and write imperatively, exactly as `makeVar` did.
 *
 * `set` skips the broadcast when the value is unchanged, matching `makeVar`'s equality check so
 * the `useSyncExternalStore` snapshot stays referentially stable and no spurious re-renders fire.
 */
export function createExternalStore<T>(initialValue: T): ExternalStore<T> {
  let value = initialValue;
  const listeners = new Set<() => void>();

  const get = (): T => value;

  const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };

  const set = (next: T): void => {
    if (next === value) {
      return;
    }
    value = next;
    listeners.forEach((listener) => listener());
  };

  return {
    get,
    set,
    subscribe,
    useValue: () => useSyncExternalStore(subscribe, get, get),
  };
}
